
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { shouldSpaceSelectSuggestion } from './autocomplete';
import { ConsoleSuggestion } from './types';

@Component
export default class CommandInput extends Vue {
  @Prop({ type: String, default: '' })
  public value!: string;

  @Prop({ type: Array, default: () => [] })
  public suggestions!: ConsoleSuggestion[];

  @Prop({ type: Boolean, default: false })
  public loading!: boolean;

  @Prop({ type: Boolean, default: false })
  public disabled!: boolean;

  @Prop({ type: String, default: 'Type /help, /find example.com, or a domain…' })
  public placeholder!: string;

  @Prop({ type: Array, default: () => [] })
  public history!: string[];

  public activeIndex = 0;
  public showSuggestions = false;
  public focused = false;
  public suggestionStyle: { [key: string]: string } = {};

  private historyIndex = -1;
  private draftBeforeHistory = '';
  private listboxId = `console-suggest-${Math.random().toString(36).slice(2, 9)}`;
  private onViewportChangeBound: (() => void) | null = null;

  public get activeDescendant(): string | undefined {
    if (!this.showSuggestions || !this.suggestions.length) {
      return undefined;
    }
    return this.optionId(this.activeIndex);
  }

  /** Default highlight is the bottom item (closest to the input). */
  private resetActiveToBottom(): void {
    this.activeIndex = this.suggestions.length ? this.suggestions.length - 1 : 0;
  }

  @Watch('suggestions')
  public onSuggestionsChange(): void {
    this.resetActiveToBottom();
    this.showSuggestions = this.focused && this.suggestions.length > 0;
    this.$nextTick(() => {
      this.updateSuggestionPosition();
      this.scrollActiveIntoView(true);
    });
  }

  @Watch('showSuggestions')
  public onShowSuggestionsChange(): void {
    this.$nextTick(() => {
      this.updateSuggestionPosition();
      if (this.showSuggestions) {
        this.scrollActiveIntoView(true);
      }
    });
  }

  public mounted(): void {
    this.onViewportChangeBound = () => this.updateSuggestionPosition();
    window.addEventListener('resize', this.onViewportChangeBound);
    window.addEventListener('scroll', this.onViewportChangeBound, true);
  }

  public beforeDestroy(): void {
    if (this.onViewportChangeBound) {
      window.removeEventListener('resize', this.onViewportChangeBound);
      window.removeEventListener('scroll', this.onViewportChangeBound, true);
      this.onViewportChangeBound = null;
    }
  }

  public optionId(index: number): string {
    return `${this.listboxId}-opt-${index}`;
  }

  public focus(): void {
    const el = this.$refs.input as HTMLInputElement | undefined;
    if (el) {
      el.focus();
    }
  }

  public onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.historyIndex = -1;
    this.draftBeforeHistory = '';
    this.$emit('input', target.value);
    this.showSuggestions = true;
    this.$nextTick(() => this.updateSuggestionPosition());
  }

  public onFocus(): void {
    this.focused = true;
    this.showSuggestions = this.suggestions.length > 0;
    this.$emit('focus');
    this.$nextTick(() => this.updateSuggestionPosition());
  }

  public onBlur(): void {
    this.focused = false;
    // Delay so mousedown on suggestion can fire first.
    window.setTimeout(() => {
      this.showSuggestions = false;
    }, 120);
  }

  public selectSuggestion(suggestion: ConsoleSuggestion): void {
    this.$emit('select-suggestion', suggestion);
    this.showSuggestions = false;
    this.$nextTick(() => this.focus());
  }

  public updateSuggestionPosition(): void {
    if (!this.showSuggestions || !this.suggestions.length) {
      return;
    }
    const row = this.$refs.row as HTMLElement | undefined;
    if (!row) {
      return;
    }
    const rect = row.getBoundingClientRect();
    const maxHeight = 280;
    const gap = 4;
    const top = Math.max(8, rect.top - gap);
    this.suggestionStyle = {
      position: 'fixed',
      left: `${Math.round(rect.left)}px`,
      width: `${Math.round(rect.width)}px`,
      bottom: `${Math.round(window.innerHeight - top)}px`,
      maxHeight: `${maxHeight}px`,
      zIndex: '200',
    };
  }

  public scrollActiveIntoView(alignToEnd: boolean): void {
    if (!this.showSuggestions || !this.suggestions.length) {
      return;
    }
    const listbox = this.$refs.listbox as HTMLElement | undefined;
    if (!listbox) {
      return;
    }
    const option = document.getElementById(this.optionId(this.activeIndex));
    if (!option) {
      return;
    }

    if (alignToEnd || this.activeIndex === this.suggestions.length - 1) {
      listbox.scrollTop = listbox.scrollHeight;
      return;
    }

    if (this.activeIndex === 0) {
      listbox.scrollTop = 0;
      return;
    }

    const listRect = listbox.getBoundingClientRect();
    const optionRect = option.getBoundingClientRect();

    if (optionRect.top < listRect.top) {
      listbox.scrollTop -= (listRect.top - optionRect.top);
    } else if (optionRect.bottom > listRect.bottom) {
      listbox.scrollTop += (optionRect.bottom - listRect.bottom);
    }
  }

  /** Space selects only when suggestion.spaceSelect or completing first token (/cmd). */
  public shouldSpaceSelect(suggestion: ConsoleSuggestion): boolean {
    return shouldSpaceSelectSuggestion(this.value, suggestion);
  }

  public onKeydown(event: KeyboardEvent): void {
    const hasSuggestions = this.showSuggestions && this.suggestions.length > 0;

    // Popup sits above the input: start at bottom, ArrowUp moves toward the top of the list.
    if (event.key === 'ArrowUp' && hasSuggestions) {
      event.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + this.suggestions.length) % this.suggestions.length;
      this.$nextTick(() => this.scrollActiveIntoView(false));
      return;
    }

    if (event.key === 'ArrowDown' && hasSuggestions) {
      event.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % this.suggestions.length;
      this.$nextTick(() => this.scrollActiveIntoView(false));
      return;
    }

    if (event.key === 'Tab' && hasSuggestions) {
      event.preventDefault();
      this.selectSuggestion(this.suggestions[this.activeIndex]);
      return;
    }

    if (event.key === ' ' && hasSuggestions) {
      const active = this.suggestions[this.activeIndex];
      if (active && this.shouldSpaceSelect(active)) {
        event.preventDefault();
        this.selectSuggestion(active);
      }
      // else: let Space insert normally
      return;
    }

    if (event.key === 'Escape') {
      if (hasSuggestions) {
        event.preventDefault();
        this.showSuggestions = false;
      }
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (hasSuggestions && this.suggestions[this.activeIndex] && event.shiftKey) {
        this.selectSuggestion(this.suggestions[this.activeIndex]);
        return;
      }
      this.showSuggestions = false;
      this.$emit('submit');
      return;
    }

    // History navigation when suggestions are closed
    if (!hasSuggestions && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      if (!this.history.length) {
        return;
      }
      event.preventDefault();

      if (event.key === 'ArrowUp') {
        if (this.historyIndex === -1) {
          this.draftBeforeHistory = this.value;
          this.historyIndex = 0;
        } else if (this.historyIndex < this.history.length - 1) {
          this.historyIndex += 1;
        }
        this.$emit('input', this.history[this.historyIndex] || '');
        return;
      }

      // ArrowDown
      if (this.historyIndex === -1) {
        return;
      }
      if (this.historyIndex === 0) {
        this.historyIndex = -1;
        this.$emit('input', this.draftBeforeHistory);
        return;
      }
      this.historyIndex -= 1;
      this.$emit('input', this.history[this.historyIndex] || '');
    }
  }
}
